import { useDraggable } from '@dnd-kit/core'
import { uniqueId } from 'lodash'
import { ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react'
import { SliderLayoutType } from '..'
import useNodeWidth from '../../../../hooks/use-node-width'
import ControlSize from '../../../../types/control-size'
import { Container, HandleCircle, HandleRectangle } from './style'

type Props = {
    children?: ReactNode
    className?: string
    disabled?: boolean
    dndId?: string
    handleColor: string
    layout: SliderLayoutType
    size: ControlSize
    trackColor: string
    value?: number
    onChange?: (value: number) => void
}

const SliderDnd = ({
    className,
    disabled = false,
    dndId,
    handleColor = '#fff',
    layout,
    size,
    trackColor,
    value: valueProp = 0,
    onChange,
}: Props) => {
    const idRef = useRef<string>(dndId || uniqueId('slider'))
    const baseValueRef = useRef<number>(valueProp)
    const Handle = layout === 'circle' ? HandleCircle : HandleRectangle

    const [value, setValue] = useState<number>(valueProp)
    const { attributes, isDragging, listeners, node, setNodeRef, transform } = useDraggable({
        disabled,
        id: idRef.current,
        data: { control: 'slider', controlId: idRef.current, controlValue: value },
    })
    const width = useNodeWidth(node)

    const onMouseDown = (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => {
        const { pageX } = e.nativeEvent
        const { left } = e.currentTarget.getBoundingClientRect()

        baseValueRef.current = (pageX - left) / width
        setValue(baseValueRef.current)
    }

    useEffect(() => {
        if (!transform || !isDragging) return

        const valueShift = transform.x / width
        setValue(Math.max(0, Math.min(1, baseValueRef.current + valueShift)))
    }, [transform?.x])

    useEffect(() => {
        baseValueRef.current = valueProp
        setValue(valueProp)
    }, [valueProp])

    useEffect(() => {
        if (!isDragging && value !== valueProp) {
            onChange?.(value)
        }
    }, [isDragging])

    return (
        <Container
            {...listeners}
            {...attributes}
            className={className}
            controlLayout={layout}
            controlSize={size}
            ref={setNodeRef}
            trackColor={trackColor}
            onMouseDown={onMouseDown}
        >
            <Handle style={{ backgroundColor: handleColor, transform: `translateX(${width * value}px)` }} />
        </Container>
    )
}

export default SliderDnd
